import { useCallback, useEffect, useRef, useState } from 'react'

// Minimal Web Speech API (SpeechRecognition) hook. Client-side, no key. Used by
// the Calcutta AI entry bar (PC) and the phone remote so "Speak" and "Type" are
// the same path. onResult(finalText) fires once a final transcript arrives.
export function useSpeech({ onResult } = {}) {
  const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null
  const supported = !!SR
  const [listening, setListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const recRef = useRef(null)
  const onResultRef = useRef(onResult)
  useEffect(() => { onResultRef.current = onResult }, [onResult])

  const start = useCallback(() => {
    if (!SR) return
    try {
      const rec = new SR()
      rec.lang = 'en-US'
      rec.interimResults = true
      rec.continuous = false
      rec.maxAlternatives = 1
      rec.onresult = (e) => {
        let interim = '', final = ''
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i]
          if (r.isFinal) final += r[0].transcript
          else interim += r[0].transcript
        }
        setTranscript(final || interim)
        if (final) onResultRef.current?.(final.trim())
      }
      rec.onend = () => setListening(false)
      rec.onerror = () => setListening(false)
      recRef.current = rec
      rec.start()
      setListening(true)
      setTranscript('')
    } catch { setListening(false) }
  }, [SR])

  const stop = useCallback(() => { try { recRef.current?.stop() } catch { /* ignore */ } setListening(false) }, [])
  const reset = useCallback(() => setTranscript(''), [])
  useEffect(() => () => { try { recRef.current?.stop() } catch { /* ignore */ } }, [])

  return { supported, listening, transcript, start, stop, reset }
}
